import { Clock } from "lucide-react";
import { HGStacks } from "@/stack/stacks";
import { useNowTick } from "@/common/hooks";
import { getNow } from "@/main";
import {
  QUICK_INTERVALS,
  QuickIntervalPreset,
  quickIntervalIsActive,
} from "@/common/quickIntervals";
import {
  isFittedToAllRecords,
  useAllRecordsTarget,
} from "@/common/allRecordsRange";
import { SidebarGroupAction } from "../sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../dropdown-menu";

export default function QuickIntervals({
  stacks,
  onNavigate,
}: {
  stacks: HGStacks;
  /** Fired after a preset is applied, so the caller can re-center the list. */
  onNavigate?: () => void;
}) {
  const interval = stacks.getInterval();
  const endInterval = stacks.getEndInterval();
  useNowTick(interval.it);
  const now = getNow();

  const { target: allRecordsTarget, isFetching: isTimeRangeFetching } =
    useAllRecordsTarget();

  const apply = (preset: QuickIntervalPreset) => {
    // Resolve at click time, not from the render-time `now` snapshot, so a
    // click right before midnight can't land on the wrong day.
    const target = preset.resolve(getNow(), stacks.getInterval().it);
    if (!target) return;
    stacks.setIntervals(target.interval, target.endInterval);
    onNavigate?.();
  };

  const applyAllRecords = () => {
    if (!allRecordsTarget) return;
    stacks.setIntervals(allRecordsTarget.interval, allRecordsTarget.endInterval);
    onNavigate?.();
  };

  const allRecordsActive = isFittedToAllRecords(
    allRecordsTarget,
    interval,
    endInterval,
  );

  const activeId =
    QUICK_INTERVALS.find((preset) =>
      quickIntervalIsActive(preset, now, interval, endInterval),
    )?.id ?? "";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarGroupAction title="Quick time ranges" aria-label="Quick time ranges">
          <Clock />
        </SidebarGroupAction>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        <DropdownMenuRadioGroup
          value={activeId}
          onValueChange={(id) => {
            const preset = QUICK_INTERVALS.find((p) => p.id === id);
            if (preset) apply(preset);
          }}
        >
          {QUICK_INTERVALS.map((preset) => (
            <DropdownMenuRadioItem
              key={preset.id}
              value={preset.id}
              title={preset.title}
              className="text-[13px]"
            >
              {preset.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={!allRecordsTarget}
          onSelect={applyAllRecords}
          title={
            allRecordsTarget
              ? "Widen the range to fit all records"
              : isTimeRangeFetching
                ? "Loading record range…"
                : "No timed records here"
          }
          className={
            "text-[13px] pl-8" +
            (allRecordsActive ? " bg-accent text-accent-foreground" : "")
          }
        >
          All records
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
